import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "~/api/provider";

/**
 * BRONCO Opus 03 — facets as seat templates, thinking threads and their rooms. Every call is an RPC on the
 * KnowIt server (`{ok, data}`); refusals arrive as thrown errors carrying the server's sentence.
 */

export type FacetBinding = {
  role?: string | null;
  capability?: string | null;
  thinkerId?: string | null;
};

export type FacetSeatView = {
  rowId: string | null;
  facet: { id: string; name: string; binding?: FacetBinding | null; acts?: string[] | null };
  seat: { thinkerId: string | null; how: string | null; problem: string | null };
};

export type FacetList = {
  owner: { kind: string; thinkerId: string };
  facets: FacetSeatView[];
  acts: string[];
  capabilities: string[];
};

export type DeltaTrace = {
  delta: {
    op: string;
    kind?: string | null;
    text?: string | null;
    targets?: string[];
    why?: string | null;
  };
  accepted: boolean;
  raced?: boolean;
  reason?: string | null;
};

export type FacetTryResult = {
  roomId: string;
  facet: string;
  seat: { thinkerId: string; how: string };
  act: string;
  status: string;
  error?: string | null;
  deltas: DeltaTrace[] | null;
};

export type BoardEntry = {
  id: string;
  kind: string;
  text: string;
  status: string;
  author: string;
  certainty?: string | null;
  confirmedBy: string[];
  targets: string[];
  why?: string | null;
  options: string[];
  phase: string;
};

export type RoomEvent = {
  sequence: number;
  kind: string;
  at: string;
  data: Record<string, unknown>;
};

export type ThreadSource = { kind: string; ref: string; filter?: string | null };
export type ThreadTrigger = {
  kind: string;
  expr?: string | null;
  events?: string[] | null;
  pollMinutes?: number | null;
};
export type ThreadBudget = {
  maxTicksPerDay: number;
  maxMicroThoughtsPerTick: number;
  maxBigCallsPerDay: number;
};

export type ThreadTickRow = {
  id: string;
  roomId: string | null;
  startedAt: string;
  endedAt: string | null;
  reason: string;
  outcome: string;
  skippedWhy: string | null;
  repeats: number;
  microCount: number;
  bigCount: number;
  asked: boolean;
  questionId: string | null;
  conclusion: string | null;
  documentId: string | null;
};

export type ThreadView = {
  id: string;
  slug: string;
  title: string;
  interest: string;
  status: string;
  ownerId: string;
  sources: ThreadSource[];
  trigger: ThreadTrigger;
  budget: ThreadBudget;
  spent: { ticksToday: number; bigCallsToday: number };
  facetIds: string[];
  chairFacetId: string | null;
  big: FacetBinding | null;
  theta: number;
  collectionId: string;
  journalDocumentId: string | null;
  ticking: boolean;
  lastTickAt: string | null;
  state: {
    lastConclusion?: string | null;
    openQuestions?: string[] | null;
    lastAnswer?: string | null;
    lastBindingProblem?: string | null;
    tickRequestedAt?: string | null;
  };
  openQuestion: { id: string; key: string; title: string } | null;
  ticks: ThreadTickRow[];
  room: {
    id: string;
    status: string;
    documentId: string | null;
    modelCalls: number;
    board: { version: number; live: number; certainty: number; entries: BoardEntry[] };
    events: RoomEvent[];
  } | null;
};

export type ThreadListRow = {
  id: string;
  slug: string;
  title: string;
  status: string;
  ownerId: string;
  ticking: boolean;
  trigger: ThreadTrigger;
  lastTickAt: string | null;
  openQuestionId: string | null;
  lastTick: {
    outcome: string;
    skippedWhy: string | null;
    repeats: number;
    at: string;
    conclusion: string | null;
  } | null;
  journalDocumentId: string | null;
};

export type JournalEntry = {
  tickId: string;
  roomId: string;
  at: string;
  outcome: string;
  conclusion: string | null;
  asked: boolean;
  microCount: number;
  bigCount: number;
  note: string | null;
  document: { id: string; urlId: string; title: string } | null;
};

export type ThreadWrite = {
  id?: string;
  title?: string;
  interest?: string;
  ownerId?: string;
  collectionId?: string;
  sources?: ThreadSource[];
  trigger?: ThreadTrigger;
  budget?: ThreadBudget;
  facetIds?: string[];
  chairFacetId?: string;
  big?: FacetBinding;
  theta?: number;
  project?: string;
};

type Envelope<T> = { ok: boolean; data: T; message?: string; error?: string };

export function useRpc() {
  const api = useApi();
  return async <T>(path: string, body: unknown = {}): Promise<T> => {
    const response = (await api.research.post(path, body)) as Envelope<T>;
    if (!response.ok) {
      throw new Error(response.message ?? response.error ?? path);
    }
    return response.data;
  };
}

const KEY = ["thinkers", "threads"] as const;

export function useFacetSeats(owner: { localId?: string; personaId?: string }) {
  const rpc = useRpc();
  return useQuery({
    queryKey: ["thinkers", "facet-seats", owner.localId ?? owner.personaId],
    queryFn: () => rpc<FacetList>("facets.list", owner),
    staleTime: 15_000,
  });
}

export function useFacetTry() {
  const rpc = useRpc();
  return useMutation({
    mutationFn: (body: {
      localId?: string;
      personaId?: string;
      facetId: string;
      material: string;
      act?: string;
    }) => rpc<FacetTryResult>("facets.try", body),
  });
}

export function useThreads() {
  const rpc = useRpc();
  return useQuery({
    queryKey: [...KEY, "list"],
    queryFn: () => rpc<ThreadListRow[]>("threads.list", {}),
    staleTime: 10_000,
  });
}

/** One thread; polled every 3 s while it thinks (the live board), every 30 s otherwise. */
export function useThread(id: string | null) {
  const rpc = useRpc();
  return useQuery({
    queryKey: [...KEY, "one", id],
    queryFn: () => rpc<ThreadView>("threads.get", { id }),
    enabled: id !== null,
    refetchInterval: (query) => {
      const view = query.state.data as ThreadView | undefined;
      return view?.ticking || view?.state?.tickRequestedAt || view?.room?.status === "running"
        ? 3_000
        : 30_000;
    },
  });
}

export function useJournal(id: string | null) {
  const rpc = useRpc();
  return useQuery({
    queryKey: [...KEY, "journal", id],
    queryFn: () =>
      rpc<{ journalDocumentId: string | null; entries: JournalEntry[] }>("threads.journal", { id }),
    enabled: id !== null,
    refetchInterval: 30_000,
  });
}

/** A thread write (create · update · pause · resume · retire · tick); every write refreshes the threads. */
export function useThreadAction<TBody>(path: string) {
  const rpc = useRpc();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => rpc<unknown>(path, body),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY }),
  });
}

/** "3 micro thoughts → 1 big call → 1 question to you" — the ladder a tick climbed, in words. */
export function describeLadder(
  tick: Pick<ThreadTickRow, "microCount" | "bigCount" | "asked">,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const parts = [t("{{n}} micro thought(s)", { n: tick.microCount })];
  if (tick.bigCount > 0) parts.push(t("{{n}} big call(s)", { n: tick.bigCount }));
  if (tick.asked) parts.push(t("1 question to you"));
  return parts.join(" → ");
}
